/**
 * AccessList — ONE row anatomy for "a person who has access".
 *
 * There were five. The matter activity pane drew a 6px avatar, a name and a
 * grey badge; the project pane drew the same thing but relabelled admins
 * "Owner"; the task pane added a hover-reveal trash can; the contact card
 * stacked name over email and added a Lead badge; the manage side panel drew
 * a third size with a dropdown. They disagreed about whether the email was
 * shown, whether the owner was marked, and which casing to read a name out
 * of — so the same person looked like a different person on two pages.
 *
 * This is that row, once: avatar, name, email, an access badge, and whatever
 * the page needs to add on the end.
 *
 * READ-ONLY BY DEFAULT — and this is the point of the dedup, not an
 * omission. Removal, level changes and adding people all happen in the
 * `manageResourceCollaborators` side panel, which is transactional (stage the
 * changes, Save) and shared by every resource. A hover trash can beside a
 * read-only list is a second, immediate write path for one of those three
 * operations, on one page. `onRemove` exists for a surface that genuinely has
 * no manage panel; the panes do not pass it.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiTrashLight } from 'react-icons/pi';
import ProfileImage from '../../Header/ProfileImage';
import Badge from '../TailwindComponents/Badge';
import { BadgeColor } from '../TailwindComponents/types/badgeTypes';
import { AccessLevel, AccessPerson } from './accessModel';

const ACCESS_LABELS: Record<AccessLevel, string> = {
  view: 'View',
  edit: 'Edit',
  admin: 'Admin',
};

const ACCESS_COLORS: Record<AccessLevel, BadgeColor> = {
  view: 'gray',
  edit: 'blue',
  admin: 'slate',
};

export interface AccessListProps {
  people: AccessPerson[];
  /**
   * What an owner's badge says. "Owner" everywhere except contacts, where the
   * responsible person is the "Lead".
   */
  ownerLabel?: string;
  /** Shown in place of the list when nobody is on the record. */
  emptyText?: string;
  /** Appended to the end of a row — a Lead badge, a "shared by" line. */
  renderRowExtra?: (person: AccessPerson) => React.ReactNode;
  /**
   * Immediate per-row removal. Left unset by every pane — see the note above.
   */
  onRemove?: (person: AccessPerson) => void;
  /** Row press, when the surface has somewhere to go. */
  onSelect?: (person: AccessPerson) => void;
  className?: string;
}

const AccessList: React.FC<AccessListProps> = ({
  people,
  ownerLabel,
  emptyText,
  renderRowExtra,
  onRemove,
  onSelect,
  className,
}) => {
  const { t } = useTranslation();

  if (!people.length) {
    return (
      <p className="text-sm text-fg-muted text-center py-4">
        {emptyText || t('No one has access yet')}
      </p>
    );
  }

  return (
    <ul className={clsx('space-y-1', className)} data-testid="access-list">
      {people.map((person, index) => {
        const badgeLabel = person.isOwner
          ? t(ownerLabel || 'Owner')
          : t(ACCESS_LABELS[person.access]);

        return (
          <li
            key={person.id || person.email || index}
            data-testid="access-row"
            className={clsx(
              'group flex items-center gap-2.5 rounded-lg px-1.5 py-1.5',
              onSelect && 'cursor-pointer hover:bg-surface-sunken/70 transition-colors'
            )}
            onClick={onSelect ? () => onSelect(person) : undefined}
          >
            <ProfileImage
              profilePicture={person.avatar}
              email={person.email || null}
              initials={person.initials}
              name={person.name}
              size="w-7 h-7"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-fg-body truncate">{person.name}</p>
              {person.email && person.email !== person.name && (
                <p className="text-[11px] text-fg-muted truncate">{person.email}</p>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-shrink-0">
              {renderRowExtra?.(person)}
              <Badge color={person.isOwner ? 'indigo' : ACCESS_COLORS[person.access]}>
                {badgeLabel}
              </Badge>
              {onRemove && !person.isOwner && person.id && (
                <button
                  type="button"
                  aria-label={t('Remove')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(person);
                  }}
                  className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity
                             text-fg-muted hover:text-danger-500"
                >
                  <PiTrashLight className="h-4 w-4" />
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AccessList;
